import type { VideoItem } from './youtube-helpers';

import { Bleed, BlockStack, Box, Icon, InlineStack, Pagination, Text } from '@shopify/polaris';
import { MaximizeIcon, MinimizeIcon } from '@shopify/polaris-icons';
import { buildEmbedUrl, IFRAME_ALLOW } from './youtube-helpers';
import { useYouTubePlayback } from './useYouTubePlayback';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { cls } from '~/utils';

interface GSetupGuideVideoTutorialProps {
  videos: VideoItem[];
  initialIndex?: number;
  isGuideVisible?: boolean;
}

export const GSetupGuideVideoTutorial = ({
  videos,
  initialIndex = 0,
  isGuideVisible = true,
}: GSetupGuideVideoTutorialProps) => {
  const { t } = useTranslation();
  const {
    currentIndex,
    isFirst,
    isLast,
    isFullscreen,
    playedIndices,
    setCurrentIframeRef,
    handleIframeLoad,
    handlePrev,
    handleNext,
    enterFullscreen,
    exitFullscreen,
  } = useYouTubePlayback(videos, initialIndex, isGuideVisible);

  if (videos.length === 0) return null;

  const isActive = (index: number) => index === currentIndex;

  return (
    <Bleed marginInline="400">
      <Box
        paddingBlockStart="300"
        paddingBlockEnd="200"
        paddingInline="400"
        borderColor="border-secondary"
        borderBlockStartWidth="025"
      >
        <BlockStack gap="200">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="h4" variant="headingMd" tone="subdued">
              {t('Video tutorial')}
            </Text>

            <Pagination
              hasPrevious={!isFirst}
              onPrevious={handlePrev}
              hasNext={!isLast}
              onNext={handleNext}
            />
          </InlineStack>

          {/* Portal overlay — renders at body so position:fixed always covers viewport */}
          {isFullscreen &&
            createPortal(
              <div className="GSetupGuide-video-overlay" onClick={exitFullscreen} />,
              document.body,
            )}

          <div className="GSetupGuide-video-slider">
            <div
              className="GSetupGuide-video-track"
              style={{ transform: isFullscreen ? 'none' : `translateX(-${currentIndex * 100}%)` }}
            >
              {videos.map((video, index) => (
                <div className="GSetupGuide-video-slide" key={video.videoLink}>
                  {/* Placeholder prevents layout shift when content goes fixed */}
                  {isFullscreen && isActive(index) && (
                    <div className="GSetupGuide-video-slide-placeholder" />
                  )}

                  <div
                    className={cls(
                      'GSetupGuide-video-slide-content',
                      isFullscreen && isActive(index)
                        ? 'GSetupGuide-video-slide-content--fullscreen'
                        : undefined,
                    )}
                  >
                    <iframe
                      ref={isActive(index) ? setCurrentIframeRef : undefined}
                      src={buildEmbedUrl(video.videoLink)}
                      title={video.title}
                      onLoad={handleIframeLoad}
                      allow={IFRAME_ALLOW}
                      referrerPolicy="strict-origin-when-cross-origin"
                    />

                    {isActive(index) && playedIndices.has(index) && (
                      <button
                        className="GSetupGuide-video-fullscreen-btn"
                        style={{
                          bottom: isFullscreen ? '86px' : '56px',
                          right: isFullscreen ? '32px' : '12px',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          isFullscreen ? exitFullscreen() : enterFullscreen();
                        }}
                        aria-label={t(isFullscreen ? 'Minimize video' : 'Expand video')}
                      >
                        <Icon source={isFullscreen ? MinimizeIcon : MaximizeIcon} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </BlockStack>
      </Box>
    </Bleed>
  );
};
